import React, { useState } from 'react';
import { Button, Typography, Tag, Space, Image, Empty, Modal, Input, message, Alert, Divider } from 'antd';
import { UploadOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { UI_STATUS, uiStatusOf, uiStatusLabel, uiStatusTip, currentVersion } from './uiDesignMock';

const { Text } = Typography;
const { TextArea } = Input;

// 版本结果标签色（文案按端取）
const RESULT_COLOR = { pending: 'processing', confirmed: 'success', rejected: 'error' };

/**
 * UI 设计面板（详情页 Tab / 区域）
 * - 管理端（isService）：可上传 / 替换
 * - 企业端：待我确认时可「确认通过 / 驳回（必填理由）」
 * - 均展示设计稿画廊、说明与版本历史
 */
const UIDesignPanel = ({ state, isService, onUpload, onConfirm, onReject }) => {
    const status = uiStatusOf(state);
    const st = UI_STATUS[status];
    const last = currentVersion(state);
    const versions = state?.versions || [];
    const [rejectOpen, setRejectOpen] = useState(false);
    const [reason, setReason] = useState('');

    const handleConfirm = () => {
        Modal.confirm({
            title: '确认 UI 设计',
            content: '确认后表示认可该 UI 设计，一阶段构建将解锁；确认后不可撤销。',
            okText: '确认通过',
            cancelText: '再看一下',
            onOk: () => { onConfirm(); message.success('已确认 UI 设计'); },
        });
    };

    const handleRejectOk = () => {
        if (!reason.trim()) { message.warning('请填写驳回理由'); return; }
        onReject(reason.trim());
        setRejectOpen(false);
        setReason('');
        message.success('已驳回，等待平台修改后重新上传');
    };

    if (status === 'none') {
        return (
            <div style={{ padding: '16px 0' }}>
                <Empty description={<Text type="secondary">{isService ? '尚未上传 UI 设计' : '待平台上传 UI 设计'}</Text>}>
                    {isService && <Button type="primary" icon={<UploadOutlined />} onClick={onUpload}>上传UI设计</Button>}
                </Empty>
            </div>
        );
    }

    return (
        <div style={{ paddingTop: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <Space size={8} wrap>
                    <Tag color={st.color} style={{ border: 'none' }}>{uiStatusLabel(status, isService)}</Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        当前 v{last?.version} · 提交于 {last?.uploadedAt}（{last?.uploadedBy}）
                        {last?.result === 'confirmed' && last?.confirmedAt ? ` · 客户确认于 ${last.confirmedAt}` : ''}
                        {last?.result === 'rejected' && last?.confirmedAt ? ` · 客户驳回于 ${last.confirmedAt}` : ''}
                    </Text>
                </Space>
                <Space>
                    {isService && (
                        <Button icon={<UploadOutlined />} onClick={onUpload}>替换UI设计</Button>
                    )}
                    {!isService && status === 'pending' && (
                        <>
                            <Button type="primary" icon={<CheckOutlined />} onClick={handleConfirm}>确认通过</Button>
                            <Button danger icon={<CloseOutlined />} onClick={() => setRejectOpen(true)}>驳回</Button>
                        </>
                    )}
                </Space>
            </div>

            <div style={{ marginTop: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{uiStatusTip(status, isService)}</Text>
            </div>

            {status === 'rejected' && last?.rejectReason && (
                <Alert type="error" showIcon style={{ marginTop: 12 }} message="驳回理由" description={last.rejectReason} />
            )}
            {isService && status === 'pending' && (
                <Alert type="info" showIcon style={{ marginTop: 12 }} message="已提交，等待客户确认；确认通过后方可构建一阶段。" />
            )}

            <Divider style={{ margin: '16px 0' }} />

            <Text strong style={{ display: 'block', marginBottom: 4 }}>设计稿（当前 v{last?.version}）</Text>
            {last?.note && (
                <div style={{ marginBottom: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>说明：{last.note}</Text></div>
            )}
            <Image.PreviewGroup>
                <Space size={12} wrap>
                    {(last?.screens || []).map((s) => (
                        <div key={`${last.version}-${s.name}`} style={{ textAlign: 'center' }}>
                            <Image src={s.url} alt={s.name} width={120} height={213} style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #f0f0f0' }} />
                            <div><Text type="secondary" style={{ fontSize: 12 }}>{s.name}</Text></div>
                        </div>
                    ))}
                </Space>
            </Image.PreviewGroup>

            <Divider style={{ margin: '16px 0' }} />

            <Text strong style={{ display: 'block', marginBottom: 8 }}>版本历史（{versions.length}）</Text>
            {versions.slice().reverse().map((v) => {
                const vt = RESULT_COLOR[v.result] || RESULT_COLOR.pending;
                return (
                    <div key={v.version} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <Space size={8} wrap>
                            <Text strong style={{ fontSize: 13 }}>v{v.version}</Text>
                            <Tag color={vt} style={{ border: 'none' }}>{uiStatusLabel(v.result, isService)}</Tag>
                            <Text type="secondary" style={{ fontSize: 12 }}>{v.uploadedAt} · {v.uploadedBy} · {v.screens?.length || 0} 张</Text>
                        </Space>
                        {v.note && <div><Text type="secondary" style={{ fontSize: 12 }}>说明：{v.note}</Text></div>}
                        {v.result === 'rejected' && v.rejectReason && (
                            <div><Text type="danger" style={{ fontSize: 12 }}>驳回理由：{v.rejectReason}</Text></div>
                        )}
                        {v.result === 'confirmed' && v.confirmedAt && (
                            <div><Text type="secondary" style={{ fontSize: 12 }}>客户确认于 {v.confirmedAt}</Text></div>
                        )}
                    </div>
                );
            })}

            <Modal
                title="驳回 UI 设计"
                open={rejectOpen}
                onOk={handleRejectOk}
                onCancel={() => { setRejectOpen(false); setReason(''); }}
                okText="提交驳回"
                cancelText="取消"
                okButtonProps={{ danger: true }}
                destroyOnClose
            >
                <Text type="secondary" style={{ fontSize: 12 }}>请填写驳回理由，平台将按此修改后重新上传。</Text>
                <TextArea rows={4} style={{ marginTop: 8 }} maxLength={200} showCount
                    value={reason} onChange={(e) => setReason(e.target.value)} placeholder="请输入驳回理由（必填）" />
            </Modal>
        </div>
    );
};

export default UIDesignPanel;
